import { API_BASE_URL } from "../../../../config/api";
import { getToken } from "../../../../shared/data/storage/authStorage";
import {
  ApiEnvelope,
  CompanySettingsDTO,
  GstChecksDTO,
  GstTaxSyncResultDTO,
  GstZohoStatusDTO,
  BankSettingsDTO,
  LocationSettingsDTO,
} from "../models/CompanySettingsDTO";

/**
 * Thrown for any non-2xx response or malformed envelope.
 *
 * `fieldErrors` carries the settings validator's per-field messages so the form
 * can put "Keep this under 20 characters." next to the pincode box instead of
 * showing one anonymous banner.
 */
export class SettingsApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly fieldErrors: Record<string, string> = {}
  ) {
    super(message);
    this.name = "SettingsApiError";
  }
}

/** Base path for every v1 settings endpoint. */
const SETTINGS_API_PATH = `${API_BASE_URL}/api/v1/settings`;

/**
 * HTTP access for the tenant settings area.
 *
 * Same fetch + bearer-token pattern as the other feature data sources, so auth
 * and error handling behave identically to the rest of the app.
 */
export class SettingsApiDataSource {
  /** GET /api/v1/settings/company */
  async fetchCompanySettings(signal?: AbortSignal): Promise<CompanySettingsDTO> {
    return this.request("GET", "/company", undefined, signal);
  }

  /** PUT /api/v1/settings/company */
  async saveCompanySettings(
    changes: Record<string, string>,
    signal?: AbortSignal
  ): Promise<CompanySettingsDTO> {
    return this.request("PUT", "/company", changes, signal);
  }

  /**
   * GET /api/v1/settings/gst
   *
   * Note this is NOT `api/settings/gstin-verification`. That endpoint returns
   * the Cashfree client secret in the response body; this one never does.
   */
  async fetchGstSettings(signal?: AbortSignal): Promise<CompanySettingsDTO> {
    return this.request("GET", "/gst", undefined, signal);
  }

  /** PUT /api/v1/settings/gst */
  async saveGstSettings(
    changes: Record<string, string>,
    signal?: AbortSignal
  ): Promise<CompanySettingsDTO> {
    return this.request("PUT", "/gst", changes, signal);
  }

  /**
   * GET /api/v1/settings/gst/zoho
   *
   * ZOUR-19 — only the read-only Zoho registration block and the backlog
   * counts. Separate from `/gst` on purpose: Refresh must not replace the form
   * values and throw away what the user has typed.
   */
  async fetchGstZohoStatus(signal?: AbortSignal): Promise<GstZohoStatusDTO> {
    return this.requestAs<GstZohoStatusDTO>("GET", "/gst/zoho", undefined, signal);
  }

  /**
   * POST /api/v1/settings/gst/sync-taxes
   *
   * ZOUR-19 — pull the org's tax list from Zoho into `taxes`. The same sync the
   * off -> on flip runs by itself; this is the button for catching up later.
   */
  async syncGstTaxes(signal?: AbortSignal): Promise<GstTaxSyncResultDTO> {
    return this.requestAs<GstTaxSyncResultDTO>("POST", "/gst/sync-taxes", undefined, signal);
  }

  /**
   * POST /api/v1/settings/gst/use-zoho-gstin
   *
   * ZOUR-19 item 13 — fill a BLANK company GSTIN from Zoho Books. Returns the
   * whole settings payload, so the panel refreshes with the field filled.
   */
  async useZohoGstin(signal?: AbortSignal): Promise<CompanySettingsDTO> {
    return this.requestAs<CompanySettingsDTO>("POST", "/gst/use-zoho-gstin", undefined, signal);
  }

  /**
   * GET /api/v1/settings/gst/checks
   *
   * ZOUR-19 — the two on-demand checks. On demand because both walk tables and
   * decode JSON; report-only, so nothing here changes any data.
   */
  async fetchGstChecks(signal?: AbortSignal): Promise<GstChecksDTO> {
    return this.requestAs<GstChecksDTO>("GET", "/gst/checks", undefined, signal);
  }

  /**
   * GET /api/v1/settings/locations
   *
   * ZOUR-65 — the company's GST registrations. The first read on a tenant that
   * has never synced pulls the list from Zoho itself, so the page does not need
   * an "initialise" step of its own.
   */
  async fetchLocations(signal?: AbortSignal): Promise<LocationSettingsDTO> {
    return this.requestAs<LocationSettingsDTO>("GET", "/locations", undefined, signal);
  }

  /**
   * POST /api/v1/settings/locations/sync
   *
   * The Refresh button. Returns the refreshed panel payload, so one round trip
   * both syncs and re-reads.
   */
  async syncLocations(signal?: AbortSignal): Promise<LocationSettingsDTO> {
    return this.requestAs<LocationSettingsDTO>("POST", "/locations/sync", undefined, signal);
  }

  /**
   * PUT /api/v1/settings/locations/{id}/gst-date
   *
   * ZOUR-92 — the one writable field on the Locations panel. An empty string
   * clears it, which means "inherit the company date". Returns the refreshed
   * panel payload so the table redraws from one round trip, like the Refresh.
   */
  async saveLocationGstDate(
    id: number,
    gstEnabledFrom: string | null,
    signal?: AbortSignal
  ): Promise<LocationSettingsDTO> {
    return this.requestAs<LocationSettingsDTO>(
      "PUT",
      `/locations/${id}/gst-date`,
      { gst_enabled_from: gstEnabledFrom ?? "" },
      signal
    );
  }

  /**
   * GET /api/v1/settings/banks
   *
   * ZOUR-87 — settings that belong to the Banks panel rather than to any one
   * bank account. Its own endpoint, not a field on `/company`: that one is the
   * Company Details panel's contract.
   */
  async fetchBankSettings(signal?: AbortSignal): Promise<BankSettingsDTO> {
    return this.requestAs<BankSettingsDTO>("GET", "/banks", undefined, signal);
  }

  /** PUT /api/v1/settings/banks */
  async saveBankSettings(
    changes: Record<string, number | null>,
    signal?: AbortSignal
  ): Promise<BankSettingsDTO> {
    return this.requestAs<BankSettingsDTO>("PUT", "/banks", changes, signal);
  }

  private getAuthHeaders(): HeadersInit {
    const token = getToken();

    if (!token) {
      throw new SettingsApiError("No authentication token found", 401);
    }

    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }

  private async request(
    method: "GET" | "PUT",
    path: string,
    body?: unknown,
    signal?: AbortSignal
  ): Promise<CompanySettingsDTO> {
    return this.requestAs<CompanySettingsDTO>(method, path, body, signal);
  }

  /**
   * The same fetch, envelope check and error mapping, for a payload that is not
   * a settings form.
   *
   * ZOUR-19 added two endpoints on this panel that return their own shapes; the
   * envelope handling below is exactly what they need, so `request` now
   * delegates here rather than either endpoint growing its own copy.
   */
  private async requestAs<T>(
    method: "GET" | "PUT" | "POST",
    path: string,
    body?: unknown,
    signal?: AbortSignal
  ): Promise<T> {
    const response = await fetch(`${SETTINGS_API_PATH}${path}`, {
      method,
      headers: this.getAuthHeaders(),
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });

    let envelope: ApiEnvelope<T> | null = null;

    try {
      envelope = await response.json();
    } catch {
      // A non-JSON body from an error response is more useful reported as an
      // HTTP failure than as a parse error; the status check below does that.
      envelope = null;
    }

    if (!response.ok) {
      throw new SettingsApiError(
        envelope?.message || `Request failed with status ${response.status}`,
        response.status,
        envelope?.errors ?? {}
      );
    }

    if (!envelope?.success || !envelope?.data) {
      throw new SettingsApiError(
        envelope?.message || "Malformed company settings response",
        response.status,
        envelope?.errors ?? {}
      );
    }

    return envelope.data;
  }
}
